import React, { useState, useCallback, useMemo, useEffect } from "react";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDNextBar } from "../../../components/Admin/DDNextBar";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { DDSiteManager } from "../../../components/Admin/DDSiteManager/DDSiteManager";
import { useAuth, useDdSites } from "../../../hooks";
import { DdOcdeAe } from "../../../api";
import { DIMENSIONS, MATURITY_LABELS, AE_DEFAULT, dimAvg, maturityColor } from "./ocdeAeCatalog";
import "./SelfAssessments.scss";

const ddAeApi = new DdOcdeAe();

/* ───────────────────────────── Datos maestros (ver ./ocdeAeCatalog.js) ───────────────────────────── */

/** Devuelve una copia de `maturity` con una entrada por defecto para cualquier
 * sitio de `sites` que todavía no tenga datos cargados (agregado desde otro módulo). */
function reconcileMaturity(maturity, sites) {
  let changed = false;
  const next = { ...maturity };
  DIMENSIONS.forEach((dim) => {
    const dimEntry = { ...next[dim.id] };
    sites.forEach((s) => {
      if (!dimEntry[s.id]) { dimEntry[s.id] = { level: 1, notes: "" }; changed = true; }
    });
    next[dim.id] = dimEntry;
  });
  return { data: next, changed };
}

/* ─────────────────────────────── Componente ─────────────────────────────── */

export function SelfAssessments() {
  const { accessToken } = useAuth();
  const [maturity, setMaturity] = useState(() => JSON.parse(JSON.stringify(AE_DEFAULT)));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [currentDim, setCurrentDim] = useState(0);
  const [subView, setSubView] = useState("form");
  const [methOpen, setMethOpen] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });
  const { sites, sitesLoading, sitesError, reloadSites, addSite, removeSite } = useDdSites(accessToken);

  // Si la carga falla no se muestra (ni se guarda) la autoevaluación de demostración.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    ddAeApi.getData(accessToken)
      .then(d => { if (d?.maturity) setMaturity(d.maturity); })
      .catch((err) => { console.error("Error cargando datos de Autoevaluación", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  // Vista siempre consistente: rellena con valores por defecto cualquier sitio
  // que aún no tenga madurez cargada (recién agregado en este módulo u otro).
  const safeMaturity = useMemo(() => reconcileMaturity(maturity, sites).data, [maturity, sites]);

  // Persiste esos valores por defecto para que la próxima carga ya los traiga completos.
  useEffect(() => {
    if (loading || sitesLoading || loadError || sitesError) return;
    const { data, changed } = reconcileMaturity(maturity, sites);
    if (changed) {
      setMaturity(data);
      if (accessToken) ddAeApi.saveData(accessToken, { maturity: data }).catch((err) => console.error("Error guardando datos de Autoevaluación", err));
    }
  }, [sites, loading, sitesLoading, loadError, sitesError, maturity, accessToken]);

  const persist = useCallback((next) => {
    setMaturity(next);
    if (accessToken) ddAeApi.saveData(accessToken, { maturity: next }).catch((err) => console.error("Error guardando datos de Autoevaluación", err));
  }, [accessToken]);

  const updateMaturity = useCallback((did, sid, key, val) => {
    setMaturity((prev) => {
      const next = JSON.parse(JSON.stringify(reconcileMaturity(prev, sites).data));
      if (key === "level") next[did][sid].level = parseInt(val, 10);
      else next[did][sid].notes = val;
      if (accessToken) ddAeApi.saveData(accessToken, { maturity: next }).catch((err) => console.error("Error guardando datos de Autoevaluación", err));
      return next;
    });
  }, [accessToken, sites]);

  const resetData = () => {
    setConfirm({
      open: true,
      msg: "¿Resetear toda la Autoevaluación a los valores de ejemplo?",
      onConfirm: () => {
        const fresh = reconcileMaturity(JSON.parse(JSON.stringify(AE_DEFAULT)), sites).data;
        persist(fresh);
        setCurrentDim(0);
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  };

  /* ── Cálculos de summary ── */
  const summary = useMemo(() => {
    let totalSum = 0, totalCount = 0, gaps = 0;
    const dimAvgs = [];
    const siteAvgs = sites.map(() => ({ sum: 0, count: 0 }));
    DIMENSIONS.forEach((d) => {
      let dimSum = 0;
      sites.forEach((s, si) => {
        const lvl = safeMaturity[d.id][s.id].level;
        totalSum += lvl; totalCount++; dimSum += lvl;
        siteAvgs[si].sum += lvl; siteAvgs[si].count++;
        if (lvl < 3) gaps++;
      });
      dimAvgs.push({ dim: d, avg: dimSum / sites.length });
    });
    const globalAvg = totalSum / totalCount;
    const weakest = dimAvgs.reduce((a, b) => a.avg < b.avg ? a : b);
    const siteScored = sites.map((s, i) => ({ site: s, avg: siteAvgs[i].sum / siteAvgs[i].count }));
    const strongest = siteScored.reduce((a, b) => a.avg > b.avg ? a : b);
    return { globalAvg, gaps, weakest, strongest, dimAvgs };
  }, [safeMaturity, sites]);

  if (loading || sitesLoading) return <div className="ae-page"><DDNav /><div style={{ padding: "40px 22px", color: "#6B7280" }}>Cargando…</div></div>;
  if (loadError || sitesError) {
    return <DDLoadError className="ae-page" onRetry={() => { if (loadError) setReloadKey((k) => k + 1); if (sitesError) reloadSites(); }} />;
  }

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ maturity: safeMaturity }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sustemia-ae-" + new Date().toISOString().slice(0, 10) + ".json";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Dimensión actual ── */
  const d = DIMENSIONS[currentDim];
  const avg = dimAvg(safeMaturity, d.id, sites);
  const levels = sites.map((s) => safeMaturity[d.id][s.id].level);
  const maxIdx = levels.indexOf(Math.max(...levels));
  const minIdx = levels.indexOf(Math.min(...levels));
  const loadedCount = DIMENSIONS.filter((dim) =>
    sites.every((s) => (safeMaturity[dim.id][s.id].notes || "").trim().length > 0)
  ).length;

  return (
    <div className="ae-page">
      <DDNav />

      {/* Page header */}
      <div className="ae-ph">
        <div>
          <h1>Autoevaluación de operaciones propias</h1>
          <div className="ae-ph-sub">DD · Etapa 2.2 · Sección I.2 de la Guía OCDE 2021 · Modelo de madurez sobre 9 áreas OCDE</div>
        </div>
        <div className="ae-ph-r">
          <button className="ae-btn ae-btn--sm" onClick={exportData}>⇩ Exportar JSON</button>
          <button className="ae-btn ae-btn--sm" onClick={resetData}>↻ Reset</button>
        </div>
      </div>

      {/* Sub-view switcher */}
      <div className="ae-viewsw">
        <button className={subView === "form" ? "active" : ""} onClick={() => setSubView("form")}>📋 Cargar autoevaluación</button>
        <button className={subView === "summary" ? "active" : ""} onClick={() => setSubView("summary")}>📊 Gap analysis</button>
      </div>

      <DDSiteManager sites={sites} onAdd={addSite} onRemove={removeSite} />

      {/* Methodology panel (collapsible) */}
      <div className={`ae-meth-panel${methOpen ? " ae-meth-panel--open" : ""}`}>
        <div className="ae-mp-head" onClick={() => setMethOpen((o) => !o)}>
          <h3>📖 Metodología · cómo hacer la autoevaluación 2.2 según OCDE 2021</h3>
          <span className="ae-mp-toggle">{methOpen ? "▲" : "▼"}</span>
        </div>
        {methOpen && (
          <div className="ae-mp-body">
            <h5>📌 Fuente única de verdad</h5>
            <p>Implementa la <strong>Sección 2.2</strong> de la <em>Guía OCDE 2021</em>. Complementa el Risk Register y alimenta el CAP Tracker.</p>
            <h5>🧭 Qué dice OCDE textualmente</h5>
            <div className="ae-source">
              <p>"Si la empresa ha identificado un riesgo de daño dentro de sus propias operaciones, deberá llevar a cabo una evaluación interna para determinar el alcance del riesgo e impactos existentes sobre el terreno. (…) La empresa debe colaborar con las partes interesadas potencialmente afectadas – incluyendo sus empleados, trabajadores, sindicatos y organizaciones de representación de propia elección de los trabajadores – para identificar daños reales y potenciales en sus propias operaciones. Este es un paso fundamental."</p>
              <span className="ae-source-src">— Guía OCDE 2021, Sección 2.2 y 2.2.1</span>
            </div>
            <h5>📋 Las 9 áreas que OCDE pide revisar (lista textual)</h5>
            <ul className="ae-ocde-list">
              <li><strong>Políticas y mensajes</strong> comunicados al máximo nivel de gestión sobre los riesgos identificados</li>
              <li><strong>Prácticas de compra y de fijación de precios</strong> (Recuadro 4)</li>
              <li><strong>Reglas y procedimientos</strong> relativos a riesgos</li>
              <li><strong>Programas en vigencia</strong> para apoyar el cumplimiento normativo</li>
              <li><strong>Entrenamientos, capacidades y saberes</strong> de los empleados</li>
              <li><strong>Controles financieros</strong> (Módulo 11 sobre Cohecho y Corrupción)</li>
              <li><strong>Procedimientos de monitoreo</strong>, incluyendo sistemas de alerta roja</li>
              <li><strong>Canales de denuncia y protección contra represalias</strong></li>
              <li><strong>Procesos que habiliten reparación</strong> (OLGM)</li>
            </ul>
            <div className="ae-disclaim">
              <strong>OCDE explicita que la lista es no exhaustiva</strong>. La Guía no establece escala numérica. La <strong>escala 1-5</strong> es operacionalización Sustemia. Los criterios cualitativos sí son textuales del documento.
            </div>
            <h5>🧮 Fórmula operacional Sustemia</h5>
            <div className="ae-formula">
              <strong>Madurez por sitio</strong> = nivel 1 a 5<br />
              <strong>Madurez del grupo</strong> = promedio simple<br />
              Gap = madurez &lt; 3 · alimenta Etapa 3
            </div>
          </div>
        )}
      </div>

      {/* ── FORM VIEW ── */}
      {subView === "form" && (
        <div className="ae-formwrap">
          {/* Form header */}
          <div className="ae-formhd">
            <div className="ae-fhd-id">
              <span className="ae-code">{d.code}</span>
              <span className={`ae-maturity-pill ${maturityColor(avg)}`}>
                Promedio: {avg.toFixed(1)} · {MATURITY_LABELS[Math.round(avg)]}
              </span>
              <span className="ae-fhd-title">{d.name}</span>
            </div>
            <div className="ae-fhd-nav">
              <span className="ae-prog-label">{loadedCount}/9 dimensiones con evidencia</span>
              <button className="ae-btn ae-btn--sm" onClick={() => setCurrentDim((i) => Math.max(0, i - 1))} disabled={currentDim === 0}>← Anterior</button>
              <button className="ae-btn ae-btn--sm" onClick={() => setCurrentDim((i) => Math.min(DIMENSIONS.length - 1, i + 1))} disabled={currentDim === DIMENSIONS.length - 1}>Siguiente →</button>
            </div>
          </div>

          {/* Dimension tabs */}
          <div className="ae-tabs">
            {DIMENSIONS.map((dim, i) => {
              const tavg = dimAvg(safeMaturity, dim.id, sites);
              return (
                <button
                  key={dim.id}
                  className={`ae-tab${i === currentDim ? " ae-tab--active" : ""}`}
                  onClick={() => setCurrentDim(i)}
                >
                  {i + 1} · {dim.short}
                  <span className="ae-tab-n">{tavg.toFixed(1)}</span>
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="ae-tab-content">

            {/* Hint box */}
            <div className="ae-tabhint">
              <span className="ae-tabhint-ic">✦</span>
              <div>
                <strong>{d.ref}</strong> — {d.def}
                <div className="ae-tabhint-src">"{d.quote}" — {d.quoteSrc}</div>
              </div>
            </div>

            {/* Criteria */}
            <div className="ae-fsec">
              <h4>Criterios de evaluación según OCDE <span className="ae-h-meta">qué tiene que existir</span></h4>
              <div className="ae-criteria-list">
                {d.criteria.map((c, ci) => (
                  <div key={ci} className="ae-criteria-item">
                    <span className="ae-cnum">{ci + 1}</span>
                    <div className="ae-ctxt">
                      <strong>{c.t}.</strong> {c.d}
                      <span className="ae-cref">Fuente: {c.r}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rubric */}
            <div className="ae-rubric-block">
              <h5>📏 Rúbrica de madurez · cómo asignar 1, 2, 3, 4 o 5</h5>
              <div className="ae-rub-intro">El modelo de madurez 1-5 es operacionalización Sustemia. OCDE no establece escala numérica; los criterios cualitativos sí son textuales.</div>
              <table className="ae-rubric-table">
                <thead><tr><th className="ae-lvl-h">Nivel</th><th>Descripción operacional</th></tr></thead>
                <tbody>
                  {[
                    [1, "Ausente", "No existe la política, sistema o procedimiento. Los criterios OCDE no se cumplen."],
                    [2, "Inicial", "Documento existe pero no implementado o aplicado irregular."],
                    [3, "Definido", "Implementado parcialmente. Mayoría de criterios cumplidos sin monitoreo sistemático."],
                    [4, "Gestionado", "Implementado, monitoreado, documentado, revisado. Todos los criterios con evidencia."],
                    [5, "Optimizado", "Mejorado continuamente, auditado externamente. Excede criterios con benchmarks."],
                  ].map(([lvl, lbl, desc]) => (
                    <tr key={lvl}>
                      <td className="ae-lvl-c"><span className={`ae-lvl ae-l${lvl}`}>{lvl}</span><br />{lbl}</td>
                      <td>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ae-rubric-foot">
                <strong>Cuándo asignar cada nivel:</strong> Sin nada → 1 · Documento sin uso → 2 · Implementación parcial → 3 · Implementación con monitoreo → 4 · Auditoría externa y mejora continua → 5.
              </div>
            </div>

            {/* Per-site scoring */}
            <div className="ae-fsec">
              <h4>Madurez por sitio <span className="ae-h-meta">documentá evidencia</span></h4>
              <table className="ae-scoring-table">
                <thead>
                  <tr>
                    <th className="ae-site-h">Sitio</th>
                    <th className="ae-mat-h">Nivel de madurez</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((st) => {
                    const m = safeMaturity[d.id][st.id];
                    return (
                      <tr key={st.id}>
                        <td className="ae-site-cell">
                          <span className="ae-flag-pill">{st.flag}</span>
                          {st.name}
                          <span className="ae-meta-cell">{st.country} · {st.headcount.toLocaleString()} personas</span>
                        </td>
                        <td>
                          <select
                            className="ae-maturity-select"
                            value={m.level}
                            onChange={(e) => updateMaturity(d.id, st.id, "level", e.target.value)}
                          >
                            {[1, 2, 3, 4, 5].map((v) => (
                              <option key={v} value={v}>{v} · {MATURITY_LABELS[v]}</option>
                            ))}
                          </select>
                          <span className="ae-note-lbl">Evidencia / Justificación contextual</span>
                          <textarea
                            className="ae-notes-area"
                            placeholder="Qué política, procedimiento, KPI sostiene este nivel…"
                            value={m.notes || ""}
                            onChange={(e) => updateMaturity(d.id, st.id, "notes", e.target.value)}
                          />
                        </td>
                        <td>
                          <span className={`ae-maturity-pill ${maturityColor(m.level)}`}>
                            {m.level} · {MATURITY_LABELS[m.level]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Conclusion */}
            <div className="ae-fsec">
              <h4>Conclusión a nivel grupo</h4>
              <div className="ae-concl-grid">
                <div className={`ae-concl-card ${avg < 3 ? "ae-cc--r" : avg < 4 ? "ae-cc--y" : "ae-cc--g"}`}>
                  <div className="ae-cc-lbl">Madurez promedio</div>
                  <div className="ae-cc-vl" style={{ color: avg < 3 ? "var(--ae-r)" : avg >= 4 ? "var(--ae-g)" : undefined }}>{avg.toFixed(2)}</div>
                  <div className="ae-cc-meta">{MATURITY_LABELS[Math.round(avg)]}</div>
                </div>
                <div className="ae-concl-card">
                  <div className="ae-cc-lbl">Estado del grupo</div>
                  <div className="ae-cc-vl ae-cc-vl--sm">
                    <span className={`ae-maturity-pill ${maturityColor(avg)}`}>{MATURITY_LABELS[Math.round(avg)]}</span>
                  </div>
                  <div className="ae-cc-meta">{avg < 3 ? "Gap · a Etapa 3" : avg >= 4 ? "Madurez aceptable" : "En desarrollo"}</div>
                </div>
                <div className="ae-concl-card ae-cc--g">
                  <div className="ae-cc-lbl">Sitio más maduro</div>
                  <div className="ae-cc-vl ae-cc-vl--sm">{sites[maxIdx].flag} · {sites[maxIdx].name}</div>
                  <div className="ae-cc-meta">nivel {levels[maxIdx]}</div>
                </div>
                <div className="ae-concl-card ae-cc--r">
                  <div className="ae-cc-lbl">Sitio con mayor gap</div>
                  <div className="ae-cc-vl ae-cc-vl--sm">{sites[minIdx].flag} · {sites[minIdx].name}</div>
                  <div className="ae-cc-meta">nivel {levels[minIdx]}</div>
                </div>
              </div>
              {avg < 3 ? (
                <div className="ae-gap-box">
                  <div className="ae-gap-ttl">⚠ Gap significativo · acción en CAP Tracker</div>
                  <p>Madurez <strong>{avg.toFixed(2)}</strong>, debajo del umbral 3. Alimenta el CAP Tracker.</p>
                </div>
              ) : avg < 4 ? (
                <div className="ae-action-box">
                  <div className="ae-action-ttl">⚡ Madurez en desarrollo</div>
                  <p>Sugerido: monitoreo sistemático y revisión periódica.</p>
                </div>
              ) : (
                <div className="ae-ok-box">
                  <div className="ae-ok-ttl">✓ Madurez aceptable</div>
                  <p>Próximo: avanzar hacia "Optimizado" con auditoría externa.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {subView === "form" && <DDNextBar title="Siguiente · Plan de Acción 3.1" text="Definí acciones correctivas para los riesgos y gaps detectados." button="Ir al Plan de Acción" to="/admin/ocde-cap" />}

      {/* ── SUMMARY VIEW ── */}
      {subView === "summary" && (
        <div className="ae-formwrap">
          <div className="ae-formhd">
            <div className="ae-fhd-id">
              <span className="ae-code">AE-RESUMEN</span>
              <span className="ae-maturity-pill ae-m4">Gap Analysis</span>
              <span className="ae-fhd-title">Salida de Etapa 2.2 para alimentar Etapa 3</span>
            </div>
          </div>

          {/* Global KPIs */}
          <div className="ae-concl-grid ae-concl-grid--sum">
            <div className="ae-concl-card ae-cc--g">
              <div className="ae-cc-lbl">Madurez global</div>
              <div className="ae-cc-vl">{summary.globalAvg.toFixed(2)}</div>
              <div className="ae-cc-meta">9 dim × {sites.length} {sites.length === 1 ? "sitio" : "sitios"}</div>
            </div>
            <div className="ae-concl-card ae-cc--r">
              <div className="ae-cc-lbl">Gaps prioritarios</div>
              <div className="ae-cc-vl" style={{ color: "var(--ae-r)" }}>{summary.gaps}</div>
              <div className="ae-cc-meta">madurez &lt; 3</div>
            </div>
            <div className="ae-concl-card ae-cc--y">
              <div className="ae-cc-lbl">Dimensión más débil</div>
              <div className="ae-cc-vl ae-cc-vl--sm">{summary.weakest.dim.short}</div>
              <div className="ae-cc-meta">madurez {summary.weakest.avg.toFixed(2)}</div>
            </div>
            <div className="ae-concl-card ae-cc--g">
              <div className="ae-cc-lbl">Sitio más maduro</div>
              <div className="ae-cc-vl ae-cc-vl--sm">{summary.strongest.site.flag} · {summary.strongest.site.name}</div>
              <div className="ae-cc-meta">madurez {summary.strongest.avg.toFixed(2)}</div>
            </div>
          </div>

          {/* Heatmap */}
          <div className="ae-fsec ae-fsec--sum">
            <h4>Heatmap · madurez por dimensión × sitio</h4>
            <div className="ae-heatmap-wrap">
              <table className="ae-heatmap">
                <thead>
                  <tr>
                    <th className="ae-dim-h">Dimensión OCDE 2.2</th>
                    {sites.map((s) => <th key={s.id}>{s.name}</th>)}
                    <th>Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {DIMENSIONS.map((dim, di) => {
                    let dimSum = 0;
                    return (
                      <tr key={dim.id} onClick={() => { setSubView("form"); setCurrentDim(di); }} className="ae-heat-row">
                        <td className="ae-dim-c">
                          {dim.name}
                          <span className="ae-dim-ref">{dim.ref}</span>
                        </td>
                        {sites.map((s) => {
                          const lvl = safeMaturity[dim.id][s.id].level;
                          dimSum += lvl;
                          return (
                            <td key={s.id}>
                              <span className={`ae-heat-cell ${maturityColor(lvl)}`}>{lvl}</span>
                            </td>
                          );
                        })}
                        <td>
                          <span className={`ae-heat-cell ${maturityColor(dimSum / sites.length)}`}>
                            {(dimSum / sites.length).toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />
    </div>
  );
}
